import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ColDef, XGrid, PageChangeParams, ValueFormatterParams, useApiRef } from '@material-ui/x-grid';
import Fetcher, { URLBuild } from '../../../services/fetcher';
import settings from '../../../settings';
import { FiltersInterface, Status, subStatus } from '../consts';
import { Button } from '@material-ui/core';
import { useNotification } from '../../../contexts/Notification';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flex: 1
  },
  moreInformationsButton: {
    display: 'flex',
	  justifyContent: 'center',
	  alignItems: 'center',
	  alignContent: 'center'
  }
}));

export interface RowDef {
  id: string,
  customerId: string,
  fullname: string,
  email: string,
  formula: string,
  interval: string,
  paymentMethod: string,
  startDate: string,
  endDate: string,
  status: string,
  details: string
};

function dateValueFormatter(params: ValueFormatterParams): string {
  const date: Date = new Date(params.value as number * 1000);
  const day = ('0' + date.getDate()).substr(-2);
  const month = ('0' + (date.getMonth() + 1)).substr(-2);
  const year = date.getFullYear();
  return day + '-' + month + '-' + year;
}

function statusFormatter(params: ValueFormatterParams): string {
  const val = subStatus.find((status: Status) => status.value === params.value);
  if (val)
    return val.displayValue;
  return '';
}

function renderSubId(params: ValueFormatterParams): JSX.Element {
  const subId = params.value;
  return (
    <div>
      <Button style={{textTransform: 'none'}} onClick={() => {
        window.open(settings.PaymentProvider.stripeDashboardUrl + 'subscriptions/' + subId, "_blank")
      }}>
        {subId}
      </Button>
    </div>
  )
}

function renderCustomerId(params: ValueFormatterParams): JSX.Element {
  const customerId = params.value;
  return (
    <div>
      <Button style={{textTransform: 'none'}} onClick={() => {
        window.open(settings.PaymentProvider.stripeDashboardUrl + 'customers/' + customerId, "_blank")
      }}>
        {customerId}
      </Button>
    </div>
  )
}

export const columns: ColDef[] = [
  { field: 'id', headerName: 'ID Abonnement', width: 200, sortable: false, renderCell: renderSubId },
  { field: 'customerId', headerName: 'ID Client Stripe', width: 200, sortable: false, renderCell: renderCustomerId },
  { field: 'fullname', headerName: 'Nom complet', width: 300, sortable: false },
  { field: 'email', headerName: 'E-mail', width: 300, sortable: false },
  { field: 'formula', headerName: 'Formule', width: 160, sortable: false },
  { field: 'interval', headerName: 'Périodicité', width: 100, sortable: false },
  { field: 'paymentMethod', headerName: 'Moyen', width: 140, sortable: false },
  { field: 'startDate', headerName: 'Date de début', width: 125, sortable: false, valueFormatter: dateValueFormatter },
  { field: 'endDate', headerName: 'Date de fin', width: 125, sortable: false, valueFormatter: dateValueFormatter },
  { field: 'status', headerName: 'Status', width: 200, sortable: false, valueFormatter: statusFormatter }
];

interface Props {
  filters: FiltersInterface
}

const Elements = (props: Props) => {
  const classes = useStyles();
  const limit = 15;
  const apiRef = useApiRef();
  const [prevPage, setPrevPage] = useState<number>(1);
  const [rows, setRows] = useState<RowDef[]>([]);
  const [nbRows, setNbRows] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [parameters, setParameters] = useState<any>({});
  const notification = useNotification();

  useEffect(() => {
    if (JSON.stringify(parameters) === JSON.stringify(props.filters))
      return;
    apiRef.current.setPage(1);
    setParameters(props.filters);
  }, [props.filters]);

  useEffect(() => {
    setLoading(true);
    Fetcher.get(
      URLBuild(settings.PaymentProvider.paymentApi + '/stripe/subscribe/list', {
        status: 'all',
        'expand': ['data.customer', 'data.customer.invoice_settings.default_payment_method'],
        'include': ['total_count'],
        limit,
        ...parameters
      })
    ).then(data => {
      setNbRows(data.total_count);
      setRows(data.data.map((row: any) => {
        let paymentMethod: string = '';
        try {
          const card = row.customer.invoice_settings.default_payment_method.card;
          paymentMethod = card ?
            `CB (${card.brand})`: '';
        } catch (_) {
          paymentMethod = ''
        }
        return {
          id: row.id,
          customerId: row.customer.id,
          fullname: row.customer.name,
          email: row.customer.email,
          formula: row.plan.nickname,
          interval: row.plan.interval,
          paymentMethod,
          startDate: row.start_date,
          endDate: row.current_period_end,
          status: row.status
        };
      }));
      setLoading(false);
    }).catch((e: Error) => {
      notification.notify(e.message, "error");
      console.error(e);
      setLoading(false);
    });
  }, [parameters]);

  const onPageChange = (paginationInfos: PageChangeParams) => {
    if (paginationInfos.rowCount === 0)
      return;
    const params: any = {...parameters };
    delete params['starting_after'];
    delete params['ending_before'];
    if (prevPage !== paginationInfos.page && rows.length !== 0) {
      if (prevPage < paginationInfos.page) {
        params['starting_after'] = rows[rows.length - 1].id;
      } else if (prevPage > paginationInfos.page) {
        params['ending_before'] = rows[0].id;
      }
    }
    setPrevPage(paginationInfos.page);
    setParameters(params);
  };

  return (
    <div className={classes.root}>
      <XGrid
        rows={rows}
        columns={columns}
        pageSize={limit}
        rowCount={nbRows}
        paginationMode="server"
        pagination={true}
        onPageChange={onPageChange}
        loading={loading}
        hideFooterPagination={loading}
        apiRef={apiRef}
        // showColumnRightBorder={true}
        // showCellRightBorder={true}
      />
    </div>
  );
}

export default Elements;